import React, { useEffect, useMemo, useState } from 'react'

import InfiniteScroll from 'react-infinite-scroll-component'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import Loading from '@components/UI/Loading'
import { NoticeEmpty } from '@components/UI/Notice'
import { usePrevious } from '@hooks/usePrevious'
import { IApplicationState } from '@store'
import {
  bulkRejectAgentSourcedCreators,
  bulkRejectAgentSourcedCreatorsStatusReset,
  fetchAgentSourcedCreators,
  fetchAgentSourcedCreatorsByToken,
} from '@store/agentSourcedCreators/actions'
import {
  selectAgentSourcedCreators,
  selectAgentSourcedCreatorsCollectionLinks,
  selectAgentSourcedCreatorsError,
  selectAgentSourcedCreatorsLoading,
  selectAgentSourcedCreatorsSortByOptions,
  selectAgentSourcedCreatorsTotal,
  selectBulkRejectLink,
  selectBulkRejectStatus,
  selectSelectedRunScope,
  selectSourcingRuns,
} from '@store/agentSourcedCreators/selectors'
import { BulkRejectRunScope } from '@store/agentSourcedCreators/types'
import { fetchCampaign } from '@store/campaigns'
import { selectIsFirstRunSetupState } from '@store/discoveryRuns/selectors'
import { selectMeInfo } from '@store/me'
import { Container, Text } from '@tribegroup'
import { isAdmin } from '@utils'
import AgentSourcedCreatorDrawer from '../AgentSourcedCreatorDrawer/AgentSourcedCreatorDrawer'
import AgentSourcedCreatorRow from '../AgentSourcedCreatorRow/AgentSourcedCreatorRow'
import DiscoveryRunForm, { IChannelState } from '../DiscoveryRunForm/DiscoveryRunForm'
import FilterBar from '../FilterBar/FilterBar'
import { PillButton } from '../PillButton.styled'
import RunTabs from '../RunTabs/RunTabs'
import { IAgentSourcedCreatorFilters } from '../types'
import { getDefaultSortDirection } from '../utils/defaultSortDirection'
import { getEffectiveSelectedRun } from '../utils/effectiveSelectedRun'
import { hasActiveFilters } from '../utils/hasActiveFilters'
import { isCompletedWithNoCreators } from '../utils/sourcingRunEmptyResult'
import { computeSourcedTotal } from '../utils/sourcingRunProgress'
import BulkRejectModal from '../VibeCheckSelectionMode/BulkRejectModal'
import VibeCheckSelectionMode from '../VibeCheckSelectionMode/VibeCheckSelectionMode'
import { ListWrapper, PageTitleShell } from './AgentSourcedCreatorListView.styled'
import ResultCount from './ResultCount'

interface IProps {
  campaignId: number
  // When set, the view fetches via the public token endpoint instead of the
  // campaign-scoped endpoint and hides brand-only chrome (filters, sort, title).
  // Used by the customer-facing /campaign-creators/:token page.
  token?: string
}

const AgentSourcedCreatorListView: React.FC<IProps> = ({ campaignId, token }) => {
  const isTokenMode = Boolean(token)
  // Keeps the token page free of any dependency on `me`. The root reducer does always
  // register the slice, so this is not crash-avoidance in the real app — but the
  // customer page is rendered from a store built ad hoc (see CampaignCreators/Page.test),
  // and not reaching for auth state on an unauthenticated page is the property we want
  // to keep true. Same conditional shape as `sortOptions` below (BRAND-9635).
  const meInfo = useSelector((state: IApplicationState) =>
    isTokenMode ? undefined : selectMeInfo(state),
  )
  // BRAND-9635: the sourced email is internal outreach data, so it needs BOTH halves.
  // Token mode is the unauthenticated customer page; and off it, brand users still
  // reach this drawer from List/Shortlisted, where internal data stays internal
  // (BRAND-9281). isAdmin is the same check that already gates the Vibe Check tab.
  const showSourcedEmail = !isTokenMode && isAdmin(meInfo)
  const dispatch = useDispatch()
  const [filters, setFilters] = useState<IAgentSourcedCreatorFilters>({
    sort_field: 'score',
    sort_direction: 'desc',
  })
  // Track id only — the actual creator object is derived from redux so it
  // refreshes when approve/reject/undo updates the slice.
  const [selectedCreatorId, setSelectedCreatorId] = useState<number | undefined>(undefined)

  const creators = useSelector(selectAgentSourcedCreators)
  const selectedCreator = selectedCreatorId
    ? creators.find((c) => c.id === selectedCreatorId)
    : undefined
  const collectionLinks = useSelector(selectAgentSourcedCreatorsCollectionLinks)
  // Sticky vibe-check total (filled from the unfiltered metadata.stats on the
  // first page); stays stable across filtering and pagination (BRAND-9328).
  // BRAND-9515 follow-up: not run-scoped and not verdict-live (see `total`
  // derivation below for why) — kept only as the pre-run-tracking fallback.
  const stickyVibeCheckTotal = useSelector(selectAgentSourcedCreatorsTotal)
  const loading = useSelector(selectAgentSourcedCreatorsLoading)
  const error = useSelector(selectAgentSourcedCreatorsError)
  const sortOptions = useSelector((state: IApplicationState) =>
    isTokenMode ? [] : selectAgentSourcedCreatorsSortByOptions(state, campaignId),
  )
  // BRAND-9306 run-aware review views: not read directly (the fetch saga
  // selects it itself), but included here so switching runs re-triggers this
  // effect and issues a fresh fetch. Contract provisional pending BRAND-9510
  // (BE) — re-verify once that PR merges.
  const selectedRunScope = useSelector(selectSelectedRunScope)
  // BRAND-9307: the selected run's status drives the RUNNING/FAILED empty
  // states below (nothing to show yet — importing — or nothing was added —
  // failed) in place of the generic "not ready" empty state.
  const sourcingRuns = useSelector(selectSourcingRuns)
  const effectiveSelectedRun = getEffectiveSelectedRun(sourcingRuns, selectedRunScope)
  // BRAND-9515 follow-up (two QA bugs, 2026-07-21): the ResultCount panel
  // ("Showing X of Y Creators") used to read `stickyVibeCheckTotal` above
  // unconditionally, which (1) tracks the BE's default/latest run rather than
  // whichever run is actually selected, and (2) only ever updates on a fresh
  // fetch — never on a single-row approve/reject/undo, unlike bulk reject
  // (whose saga re-fetches on success). sourcingRuns[].counts fixes both:
  // it's genuinely per-run (BE's sourcing_runs_summary), and the reducer's
  // agentSourcedCreatorActionSuccess case already keeps it live-synced on
  // every verdict, single or bulk — exactly what RunTabs' own record bar
  // already relies on (BRAND-9515). Falls back to the sticky total only when
  // no sourcing runs are loaded at all (legacy/pre-run-tracking briefs).
  // All-runs scope (currently unreachable — no UI control dispatches it yet,
  // see getEffectiveSelectedRun's doc comment) aggregates EVERY run's counts
  // instead of falling back to the single default run effectiveSelectedRun
  // would otherwise resolve to.
  //
  // BRAND-9592: the numerator below is `creators.length` — every row the
  // listing returned for the run, which includes creators who have answered
  // Interested (`ready_for_shortlist`; their row keeps rendering with the
  // BRAND-9587 pill). The denominator used to exclude exactly those, so the
  // two halves counted different populations and the numerator ran past the
  // denominator — "Showing 12 of 11 Creators", widening by one per Interested
  // creator.
  //
  // `computeSourcedTotal` is now the ONE definition of "how big is this run".
  // The run record bar's `computeVibeCheckProgress` delegates its `total`
  // straight to it, so the two are the same number by construction, not by
  // coincidence — they cannot drift. Call it directly here rather than going
  // through the progress helper, which would also compute a `decided` this
  // panel has no use for.
  const total =
    selectedRunScope === 'all' && sourcingRuns.length > 0
      ? computeSourcedTotal(sourcingRuns.map((r) => r.counts))
      : effectiveSelectedRun
      ? computeSourcedTotal([effectiveSelectedRun.counts])
      : stickyVibeCheckTotal
  const isSelectedRunImporting = effectiveSelectedRun?.status === 'importing'
  const isSelectedRunFailed = effectiveSelectedRun?.status === 'failed'
  // BRAND-9582 (QA item 3): completed, but sourced nobody. The BE reports this
  // as `ready_for_vibe_check` (no dedicated status exists — see
  // utils/sourcingRunEmptyResult), so without this the list fell through to
  // the generic "not ready yet" empty state while the record bar's pill said
  // READY FOR VIBE CHECK — two different wrong answers for the same run.
  const selectedRunHasNoCreators = effectiveSelectedRun
    ? isCompletedWithNoCreators(effectiveSelectedRun)
    : false
  // BRAND-9515. Ted, verbatim: "Filters are showing." — an importing/failed
  // selected run is empty BY DEFINITION, not by filtering, so the channel
  // pills/filter dropdowns/sort control (FilterBar/ResultCount) are dead
  // chrome above the dedicated RUNNING/FAILED empty state. A ready run
  // ('ready_for_vibe_check'/'vibe_check_completed') keeps this chrome even
  // when filters happen to produce 0 results — that's a filtering-empty,
  // which IS something the chrome is for. Mirrors the existing
  // isFirstRunSetupState hiding (zero runs), extended to the selected-run
  // case.
  const showFilterChrome = !isTokenMode && !isSelectedRunImporting && !isSelectedRunFailed
  // BRAND-9307: zero sourcing runs + sourcing enabled/legal for this brief —
  // show the inline first-run setup form instead of the normal run-aware
  // listing chrome (RunTabs/FilterBar/ResultCount) below. Never true in
  // token mode (the customer-facing page never sets up discovery runs).
  const isFirstRunSetupState = useSelector((state: IApplicationState) =>
    isTokenMode ? false : selectIsFirstRunSetupState(state, campaignId),
  )

  // BRAND-9514 bulk reject. Capability is link-driven: the BE offers
  // `bulk_reject` on the COLLECTION response only while the run is decidable
  // (ready_for_vibe_check, >=1 pending) — link presence IS the permission.
  // State home: the selection lives HERE as plain local state (this component
  // already owns the rows it decorates), not in a context — the Inbox needs
  // withSelectedSubmissions because its selection spans distant subtrees;
  // this list has no such consumers.
  const bulkRejectLink = useSelector(selectBulkRejectLink)
  // BRAND-9550 follow-up (gating fix): link presence already gates the entry
  // control below (capability is link-driven, per the comment above) — but
  // `selectionMode` is STATE, so it outlives the render that set it. A
  // mid-session refetch that drops the link (the run stops being decidable,
  // or the BE-side condition this link depends on flips off) must not leave
  // the selection bar or row checkboxes rendering against a stale
  // `selectionMode=true`. All three surfaces key off this SAME link-presence
  // check rather than trusting `selectionMode` alone.
  const hasBulkRejectLink = Boolean(bulkRejectLink)
  const bulkRejectStatus = useSelector(selectBulkRejectStatus)
  const [selectionMode, setSelectionMode] = useState(false)
  // BRAND-9550 follow-up (render-gap fix): `selectionMode` is state, so it can
  // briefly be true for one render while `hasBulkRejectLink` has already
  // flipped false (a successful bulk reject that consumes the last pending
  // creators, or a mid-session link flip) — the effect below clears
  // `selectionMode` asynchronously, AFTER that render has already committed.
  // Both the selection block (~595) and the filter/sort chrome block (~642)
  // used to key off `selectionMode` alone (negated for the latter), so for
  // that one frame the selection block hid (its own `hasBulkRejectLink`
  // check) AND the chrome block hid (`!selectionMode` was false) — neither
  // rendered. Deriving this once and using it in both places (plus the row
  // prop) means the chrome renders instead of nothing for that frame; the
  // exit guard still does its job of clearing the underlying state right
  // after.
  const isSelecting = selectionMode && hasBulkRejectLink
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [bulkRejectModalOpen, setBulkRejectModalOpen] = useState(false)
  // BRAND-9550 select-all-pending across pages. This is a FLAG, never a list:
  // the vibe-check list is BE-paginated, so an id list can only ever name the
  // loaded page (Archer repro: 23 of 52, then 25 of 64, both with a success
  // toast and the unloaded pendings left untouched). Escalating hands the BE
  // the scope the user is viewing — run + active filters — and lets it
  // resolve the pending set server-side, so selecting all 64 is literal
  // without loading all 64. `selectedIds` is deliberately NOT expanded when
  // this flips: the two are alternative request forms, mutually exclusive on
  // the wire.
  const [selectAllScope, setSelectAllScope] = useState(false)
  // The escalation covers whatever the user is looking at, so its copy has to
  // say which — 'all' when viewing every run, 'run' otherwise (a specific
  // ordinal, or the default latest run).
  const runScopeLabel: 'all' | 'run' = selectedRunScope === 'all' ? 'all' : 'run'
  // BRAND-9550 defect fix — bind the run CLIENT-side, at confirm time.
  //
  // An explicit choice (run-pill click / `?run=` deep link, including 'all')
  // travels verbatim, exactly as before. The DEFAULT scope — `selectedRunScope
  // === null` — is resolved HERE to the ordinal of the run actually on screen
  // instead of being omitted from the request. Omitting it left the BE to
  // re-derive "the default run" AT WRITE TIME from live DB state, so a rerun
  // finishing its import behind an open tab moved the target: the AM confirmed
  // on Run 1 and Run 2's creators were rejected.
  //
  // `null` is the ORDINARY case, not an edge one: it is the initial value, a
  // plain page load never changes it, and starting a new run resets it back —
  // so this path is what most select-alls take. `getEffectiveSelectedRun` is
  // the same resolution the list itself renders from (see
  // `effectiveSelectedRun` above), which is what makes "the run on screen" and
  // "the run in the request" the same run by construction rather than by
  // coincidence.
  //
  // `undefined` means no run could be resolved (runs not loaded yet). That is
  // NOT a licence to fall back to omitting the key — that is the defect — so
  // it gates the escalation offer below and blocks the request at confirm.
  const bulkRejectRunScope: BulkRejectRunScope | undefined =
    selectedRunScope === null ? effectiveSelectedRun?.ordinal : selectedRunScope

  // Only PENDING creators are selectable; a selected row that gets decided
  // (e.g. from another tab) silently drops out of every derived value below —
  // count, disabled state, and the submitted ids. Memoised (Warren review):
  // the list re-renders on scroll/hover/drawer-open, and these two O(creators)
  // passes are unused in the common non-selecting case. The deps keep the
  // confirm-time re-derivation guarantee intact — any creators/selection
  // change recomputes before the confirm handler can read a stale value.
  const pendingIds = useMemo(
    () => creators.filter((c) => c.status === 'pending').map((c) => c.id),
    [creators],
  )
  const selectedPendingIds = useMemo(
    () => pendingIds.filter((id) => selectedIds.includes(id)),
    [pendingIds, selectedIds],
  )

  const exitSelectionMode = (): void => {
    setSelectionMode(false)
    setSelectedIds([])
    setBulkRejectModalOpen(false)
    // BRAND-9550: the scope flag is part of the selection — it must never
    // survive a cancel/success/context change into the next pass.
    setSelectAllScope(false)
  }

  // The saga owns the request lifecycle; the view reacts to the status
  // transitions. Success exits selection (the saga also re-fetches the list/
  // counts/runs summary and toasts); a 422 failure only dismisses the modal —
  // the BE is all-or-nothing (zero changes), so the selection is KEPT for the
  // user to adjust (the saga toasts the BE's verbatim message).
  const prevBulkRejectStatus = usePrevious(bulkRejectStatus)
  useEffect(() => {
    if (prevBulkRejectStatus !== 'pending') return
    if (bulkRejectStatus === 'succeeded') {
      exitSelectionMode()
    } else if (bulkRejectStatus === 'failed') {
      setBulkRejectModalOpen(false)
    } else {
      return
    }
    // Warren review: scrub the terminal status back to 'idle' now that this
    // effect has consumed the transition, so no stale 'succeeded'/'failed'
    // lingers in the global slice for a future reader. Dispatched HERE
    // (post-render) rather than reset in the reducer on fetchCreators: the
    // saga puts success + refetch synchronously in one task, and React 19's
    // automatic batching would collapse that into a single render in which
    // this effect never observes 'succeeded'.
    dispatch(bulkRejectAgentSourcedCreatorsStatusReset())
  }, [bulkRejectStatus])

  // Adversarial finding 4a: RunTabs stay interactive during selection, and a
  // run-scope switch refetches and REPLACES creators/collectionLinks under the
  // open selection/modal. The selection is inherently per-run — exit selection
  // mode (clearing the selection and closing the modal) the moment the scope
  // changes, alongside the refetch the effect below issues for the same change.
  const prevSelectedRunScope = usePrevious(selectedRunScope)
  useEffect(() => {
    if (prevSelectedRunScope === selectedRunScope) return
    if (selectionMode || bulkRejectModalOpen) {
      exitSelectionMode()
    }
  }, [selectedRunScope])

  // Warren review (PR #3487): the run-scope teardown above was the only
  // context change that exited selection. The selected run flipping to
  // importing/failed (e.g. a retry on the SAME run — no scope change) hides
  // the filter chrome, and with it the selection bar's Cancel, while
  // selectionMode silently stays true — exit cleanly instead. The sibling
  // "+ New run" path is handled at its source (onRequestNewRun below).
  //
  // BRAND-9550 follow-up (gating fix): the bulk_reject link disappearing
  // mid-session — a refetch after the run stops being decidable, or the
  // BE-side condition it depends on flips off — is the same shape of
  // problem: nothing else re-renders to force selectionMode back to false,
  // so a live Reject button would otherwise survive the link going away.
  // Folded into the same guard rather than a second effect.
  useEffect(() => {
    if ((!showFilterChrome || !hasBulkRejectLink) && (selectionMode || bulkRejectModalOpen)) {
      exitSelectionMode()
    }
  }, [showFilterChrome, hasBulkRejectLink])

  const toggleSelect = (creatorId: number): void => {
    // Under a scope flag there is no per-row selection to toggle (v1 ships
    // without deselect-after-select-all; `except_ids` is a fast-follow).
    if (selectAllScope) return
    setSelectedIds((prev) =>
      prev.includes(creatorId) ? prev.filter((id) => id !== creatorId) : [...prev, creatorId],
    )
  }

  const handleBulkRejectConfirm = (comment: string): void => {
    if (!bulkRejectLink) {
      // Adversarial finding 4b: a silent return here left the modal open with
      // a confirm button that did NOTHING (the link vanished out from under
      // the modal). There is nothing left to reject — resolve the UI instead
      // of dangling.
      exitSelectionMode()
      return
    }
    if (selectAllScope) {
      if (bulkRejectRunScope === undefined) {
        // Fail-safe (BRAND-9550). No run could be resolved, so there is no
        // honest binding to send — and the only alternative binding, omitting
        // `sourcing_run`, is the defect itself. Refuse to send the destructive
        // request at all. Unreachable in practice (the escalation is withheld
        // in the same condition below, and `sourcingRuns` is sticky, so a
        // resolvable run cannot become unresolvable while the modal sits
        // open), which is why it resolves the UI rather than explaining
        // itself: same treatment as the vanished-link guard above, and for the
        // same reason — an inert confirm button is worse than a clean exit.
        exitSelectionMode()
        return
      }
      // BRAND-9550: submit the SCOPE, not a set. No `ids` key at all — the two
      // forms are mutually exclusive on the BE (both or neither -> 422
      // invalid_bulk_reject_ids). `sourcingRun` names the CONCRETE run the
      // user is viewing (never an absence — see bulkRejectRunScope); `filters`
      // mirror the active filter chrome, so the BE resolves precisely the set
      // the screen is describing, and nothing about that set can shift between
      // this click and the write.
      dispatch(
        bulkRejectAgentSourcedCreators({
          campaignId,
          allPending: true,
          sourcingRun: bulkRejectRunScope,
          ...(comment ? { comment } : {}),
          filters,
          href: bulkRejectLink.href,
          method: bulkRejectLink.method,
        }),
      )
      return
    }
    // Stale-closure guard (TRIBE FE stale-closure family): `selectedPendingIds`
    // is re-derived every render from the CURRENT creators + selection, so the
    // confirm submits what is live at CONFIRM time — never what was selected
    // when the modal opened. A creator decided while the modal sat open has
    // already dropped out (422 fodder).
    if (selectedPendingIds.length === 0) {
      exitSelectionMode()
      return
    }
    dispatch(
      bulkRejectAgentSourcedCreators({
        campaignId,
        ids: selectedPendingIds,
        ...(comment ? { comment } : {}),
        // Adversarial finding 1: carry the view's live filters so the saga's
        // success refetch reloads the list FILTERED, matching the chrome.
        filters,
        href: bulkRejectLink.href,
        method: bulkRejectLink.method,
      }),
    )
  }

  // BRAND-9307: explicit reveal state for RunTabs' link-driven "Retry run" /
  // "+ New run" — clicking either (see RunTabs' onRequestNewRun) opens this
  // exact DiscoveryRunForm inline, in place of the normal listing chrome,
  // pre-filled from the sourcing block already in the store. Auto-closes
  // once a new sourcing run actually appears (the run this was opened for
  // has started — the record bar above takes over showing its progress),
  // so it never lingers stale across a submit.
  const [manualRunFormOpen, setManualRunFormOpen] = useState(false)
  // DiscoveryRunForm's free-text notes, per-channel toggle/count state,
  // generateCriteria, and the draft transcript/note files are all owned
  // here (not local to that component) so a run-pill click (onRunSelect
  // below) — which unmounts the form — doesn't silently discard whatever
  // the user had typed, checked, or picked before ever attaching or
  // starting. channelState stays null until DiscoveryRunForm first derives
  // a default from the campaign's channels (see initChannelState there);
  // everything else starts blank/false.
  const [draftRunConfig, setDraftRunConfig] = useState<{
    notesText: string
    channelState: IChannelState | null
    generateCriteria: boolean
    transcriptFile: File | null
    noteFile: File | null
    noteCleared: boolean
    // BRAND-9688: draft text for the YouTube "similar handle" input, lifted
    // for the same run-pill-click reason as every other field here.
    youtubeSimilarHandle: string
  }>({
    notesText: '',
    channelState: null,
    generateCriteria: false,
    transcriptFile: null,
    noteFile: null,
    noteCleared: false,
    youtubeSimilarHandle: '',
  })
  useEffect(() => {
    setManualRunFormOpen(false)
    // The draft is CONSUMED once a run genuinely starts — same trigger as the
    // form's auto-close (a new sourcing run appearing in sourcingRuns), so
    // reopening the form for the NEXT run always starts from a blank draft.
    // Closing without starting (pill click or explicit X) must NOT reset this
    // — that's the whole point of lifting the state up.
    setDraftRunConfig({
      notesText: '',
      channelState: null,
      generateCriteria: false,
      transcriptFile: null,
      noteFile: null,
      noteCleared: false,
      youtubeSimilarHandle: '',
    })
    // sourcingRuns is tolerated as undefined (see getEffectiveSelectedRun's
    // doc comment — the token-mode CampaignCreators page preloads partial
    // state without this key); manualRunFormOpen is never true in token mode
    // anyway (RunTabs/onRequestNewRun never render there), so `?? []` here
    // just avoids a crash rather than gating real behavior.
  }, [sourcingRuns?.length ?? 0])

  useEffect(() => {
    if (isTokenMode) {
      dispatch(fetchAgentSourcedCreatorsByToken({ token: token! }))
      return
    }
    dispatch(fetchAgentSourcedCreators({ campaignId, filters }))
  }, [campaignId, filters, token, isTokenMode, selectedRunScope])

  // BRAND-9582 (QA item 10): re-sync the CAMPAIGN once a run stops importing.
  // `sourcingRuns` refreshes on every creators fetch, but "+ New run" is
  // gated on the campaign's own `start_discovery_run` rel, which the BE
  // withdraws while a run is in flight and re-issues once it finishes — and
  // the campaign is otherwise only fetched once, by CampaignProvider on
  // mount. Without this, the button stayed disabled until a hard refresh.
  // Keyed on the importing -> not-importing EDGE rather than polling, since
  // this feature has no poller. `sourcingRuns` is optional for the same
  // reason as the effect above (token-mode preloads partial state).
  const isAnyRunImporting = sourcingRuns?.some((run) => run.status === 'importing') ?? false
  const prevIsAnyRunImporting = usePrevious(isAnyRunImporting)
  useEffect(() => {
    if (isTokenMode) return
    if (prevIsAnyRunImporting && !isAnyRunImporting) {
      dispatch(fetchCampaign(campaignId))
    }
  }, [isAnyRunImporting])

  const nextLink = collectionLinks.find((l) => l.rel === 'next')
  const visible = creators.length
  const hasMore = Boolean(nextLink)

  const loadMore = (): void => {
    if (!nextLink) return
    dispatch(
      fetchAgentSourcedCreators({
        campaignId,
        filters,
        urlOverride: { href: nextLink.href, method: nextLink.method },
        append: true,
      }),
    )
  }

  const isInitialLoad = loading && creators.length === 0

  const renderContent = (): React.ReactNode => {
    if (isInitialLoad) return <Loading height={30} />
    if (error) {
      return (
        <NoticeEmpty
          alt="vibe-check"
          title={<FormattedMessage id="creatorSearch.vibeCheck.error.loadFailed.title" />}
          subtitle={<FormattedMessage id="creatorSearch.vibeCheck.error.loadFailed.subtitle" />}
        />
      )
    }
    if (creators.length === 0) {
      // BRAND-9307: while the selected run is importing/failed there's
      // nothing "not ready yet" about it in the generic sense — a dedicated
      // empty state explains why, matching the record bar's own copy above.
      if (isSelectedRunImporting) {
        return (
          <NoticeEmpty
            alt="vibe-check"
            title={<FormattedMessage id="creatorSearch.discoveryRun.empty.running.title" />}
            subtitle={<FormattedMessage id="creatorSearch.discoveryRun.empty.running.subtitle" />}
          />
        )
      }
      if (isSelectedRunFailed) {
        return (
          <NoticeEmpty
            alt="vibe-check"
            title={<FormattedMessage id="creatorSearch.discoveryRun.empty.failed.title" />}
            subtitle={<FormattedMessage id="creatorSearch.discoveryRun.empty.failed.subtitle" />}
          />
        )
      }
      // BRAND-9582: checked BEFORE the filtered/not-ready branches below —
      // the run imported nobody, so the emptiness is neither a filtering
      // result nor a "come back later", whatever the filter chrome happens to
      // be set to.
      if (selectedRunHasNoCreators) {
        return (
          <NoticeEmpty
            alt="vibe-check"
            title={<FormattedMessage id="creatorSearch.discoveryRun.empty.noCreators.title" />}
            subtitle={
              <FormattedMessage id="creatorSearch.discoveryRun.empty.noCreators.subtitle" />
            }
          />
        )
      }
      const filtered = hasActiveFilters(filters)
      return (
        <NoticeEmpty
          alt="vibe-check"
          title={
            <FormattedMessage
              id={
                filtered
                  ? 'creatorSearch.empty.noMatches.title'
                  : 'creatorSearch.vibeCheck.empty.notReady.title'
              }
            />
          }
          subtitle={
            <FormattedMessage
              id={
                filtered
                  ? 'creatorSearch.empty.noMatches.subtitle'
                  : 'creatorSearch.vibeCheck.empty.notReady.subtitle'
              }
            />
          }
        />
      )
    }
    return (
      <InfiniteScroll
        dataLength={creators.length}
        next={loadMore}
        hasMore={hasMore}
        loader={<Loading height={30} />}
        scrollThreshold={0.8}
        style={{ overflow: 'visible', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
      >
        {creators.map((creator) => (
          <AgentSourcedCreatorRow
            key={creator.id}
            creator={creator}
            campaignId={campaignId}
            onSelect={(c) => setSelectedCreatorId(c.id)}
            // BRAND-9550 follow-up (gating fix): re-check link presence here
            // rather than trusting `selectionMode` alone — see the comment by
            // `hasBulkRejectLink` above.
            selectionMode={isSelecting}
            // BRAND-9550: under a scope flag every pending row IS in the set,
            // including ones the user never ticked — show that, and lock it.
            selected={
              selectAllScope ? creator.status === 'pending' : selectedIds.includes(creator.id)
            }
            selectionLocked={selectAllScope}
            onToggleSelect={toggleSelect}
            // BRAND-9582 (QA item 16): the decision pill is brand-internal —
            // in token mode this view IS the customer-facing page, where the
            // pill would leak our verdict and its Undo control.
            showDecisionPill={!isTokenMode}
          />
        ))}
      </InfiniteScroll>
    )
  }

  return (
    <>
      <AgentSourcedCreatorDrawer
        creator={selectedCreator}
        campaignId={campaignId}
        onClose={() => setSelectedCreatorId(undefined)}
        // Second surface of QA item 16: the drawer defaults the pill to true
        // too, so token mode leaked the verdict one click deeper than the row.
        showDecisionPill={!isTokenMode}
        showSourcedEmail={showSourcedEmail}
      />
      <Container>
        {!isTokenMode && (
          <Container bottomOuterSpacing={1.5}>
            <PageTitleShell data-testid="vibe-check-list-title">
              <Text customFontSize={1.5} lineHeight={2} customletterSpacing="0.3px" color="#4d4d4d">
                <FormattedMessage id="creatorSearch.vibeCheck.list.title" />
              </Text>
            </PageTitleShell>
          </Container>
        )}

        {isFirstRunSetupState ? (
          <DiscoveryRunForm
            campaignId={campaignId}
            notesText={draftRunConfig.notesText}
            onNotesTextChange={(notesText) => setDraftRunConfig((prev) => ({ ...prev, notesText }))}
            channelState={draftRunConfig.channelState}
            onChannelStateChange={(channelState) =>
              setDraftRunConfig((prev) => ({ ...prev, channelState }))
            }
            generateCriteria={draftRunConfig.generateCriteria}
            onGenerateCriteriaChange={(generateCriteria) =>
              setDraftRunConfig((prev) =>
                generateCriteria
                  ? { ...prev, generateCriteria }
                  : // Unchecking must drop whatever was picked/typed/cleared
                    // while checked too — otherwise a picked file (or a
                    // typed-but-unsent note draft) stays displayed,
                    // unclearable (no Remove affordance once unchecked),
                    // and still gets sent on Start despite the row now
                    // reading as read-only/untouched. notesText specifically:
                    // once fix 1 (BRAND-9663 review) sends generateCriteria:
                    // false on the wire, a stray note here would 422 as
                    // attachments_not_accepted rather than just being wrong.
                    {
                      ...prev,
                      generateCriteria,
                      notesText: '',
                      transcriptFile: null,
                      noteFile: null,
                      noteCleared: false,
                    },
              )
            }
            transcriptFile={draftRunConfig.transcriptFile}
            onTranscriptFileChange={(transcriptFile) =>
              setDraftRunConfig((prev) => ({ ...prev, transcriptFile }))
            }
            noteFile={draftRunConfig.noteFile}
            onNoteFileChange={(noteFile) => setDraftRunConfig((prev) => ({ ...prev, noteFile }))}
            noteCleared={draftRunConfig.noteCleared}
            onNoteClearedChange={(noteCleared) =>
              setDraftRunConfig((prev) => ({ ...prev, noteCleared }))
            }
            youtubeSimilarHandle={draftRunConfig.youtubeSimilarHandle}
            onYoutubeSimilarHandleChange={(youtubeSimilarHandle) =>
              setDraftRunConfig((prev) => ({ ...prev, youtubeSimilarHandle }))
            }
          />
        ) : (
          <>
            {!isTokenMode && (
              <RunTabs
                campaignId={campaignId}
                onRequestNewRun={() => {
                  // Warren review: opening the discovery form is a context
                  // change too — without this, cancelling the form dropped
                  // the user straight back into the stale selection.
                  exitSelectionMode()
                  setManualRunFormOpen(true)
                }}
                // BRAND-9515: Ted's design rule, verbatim: "When we click new
                // Run the new run should be highlighted right?" — while this
                // manually-revealed form is open, "+ New run" reflects that
                // as the selected tab; selecting a run pill (onRunSelect)
                // closes the form back out and hands selection to that run.
                isDiscoveryFormOpen={manualRunFormOpen}
                onRunSelect={() => setManualRunFormOpen(false)}
              />
            )}

            {!isTokenMode && manualRunFormOpen ? (
              <DiscoveryRunForm
                campaignId={campaignId}
                onClose={() => setManualRunFormOpen(false)}
                notesText={draftRunConfig.notesText}
                onNotesTextChange={(notesText) =>
                  setDraftRunConfig((prev) => ({ ...prev, notesText }))
                }
                channelState={draftRunConfig.channelState}
                onChannelStateChange={(channelState) =>
                  setDraftRunConfig((prev) => ({ ...prev, channelState }))
                }
                generateCriteria={draftRunConfig.generateCriteria}
                onGenerateCriteriaChange={(generateCriteria) =>
                  setDraftRunConfig((prev) =>
                    generateCriteria
                      ? { ...prev, generateCriteria }
                      : {
                          ...prev,
                          generateCriteria,
                          notesText: '',
                          transcriptFile: null,
                          noteFile: null,
                          noteCleared: false,
                        },
                  )
                }
                transcriptFile={draftRunConfig.transcriptFile}
                onTranscriptFileChange={(transcriptFile) =>
                  setDraftRunConfig((prev) => ({ ...prev, transcriptFile }))
                }
                noteFile={draftRunConfig.noteFile}
                onNoteFileChange={(noteFile) =>
                  setDraftRunConfig((prev) => ({ ...prev, noteFile }))
                }
                noteCleared={draftRunConfig.noteCleared}
                onNoteClearedChange={(noteCleared) =>
                  setDraftRunConfig((prev) => ({ ...prev, noteCleared }))
                }
                youtubeSimilarHandle={draftRunConfig.youtubeSimilarHandle}
                onYoutubeSimilarHandleChange={(youtubeSimilarHandle) =>
                  setDraftRunConfig((prev) => ({ ...prev, youtubeSimilarHandle }))
                }
              />
            ) : (
              <>
                {/* BRAND-9514: while selecting, the selection bar replaces the
                    filter/sort chrome (changing filters mid-selection would
                    refetch and silently invalidate the selection). */}
                {showFilterChrome && isSelecting && (
                  <>
                    {/* Adversarial finding 2: no truthful pending-total exists
                        in state (sourcingRuns[].counts.pending is unfiltered,
                        per-run only, and goes stale after in-place single
                        decisions), and pendingIds only covers the LOADED
                        pages — so the bar states the selected count alone and
                        flags unloaded pages via hasMore instead of implying
                        "N of N" completeness. */}
                    <VibeCheckSelectionMode
                      selectedCount={selectedPendingIds.length}
                      hasMore={hasMore}
                      onSelectAllPending={() => setSelectedIds(pendingIds)}
                      // BRAND-9550: the escalation is a scope flag, NOT an
                      // expanded id list — `selectedIds` is left exactly as
                      // the user left it.
                      selectablePageCount={pendingIds.length}
                      selectAllScopeActive={selectAllScope}
                      runScopeLabel={runScopeLabel}
                      // BRAND-9550 fail-safe: with no resolvable run there is
                      // no honest scope to escalate TO, so the offer is
                      // withheld rather than degraded into the unbound
                      // request that caused the defect. Withholding the
                      // handler withholds the banner (see canEscalate) —
                      // page-select stays available throughout, so the user is
                      // never blocked from rejecting what is on screen.
                      onSelectAllScope={
                        bulkRejectRunScope === undefined ? undefined : () => setSelectAllScope(true)
                      }
                      onRejectSelected={() => {
                        if (selectAllScope || selectedPendingIds.length > 0) {
                          setBulkRejectModalOpen(true)
                        }
                      }}
                      onCancel={exitSelectionMode}
                    />
                    <BulkRejectModal
                      count={selectedPendingIds.length}
                      isOpen={bulkRejectModalOpen}
                      isRejecting={bulkRejectStatus === 'pending'}
                      allPending={selectAllScope}
                      runScopeLabel={runScopeLabel}
                      onCancel={() => setBulkRejectModalOpen(false)}
                      onConfirm={handleBulkRejectConfirm}
                    />
                  </>
                )}
                {showFilterChrome && !isSelecting && (
                  <>
                    <FilterBar mode="agent-sourced" filters={filters} onChange={setFilters} />

                    <ResultCount
                      rowTestId="vibe-check-result-count-row"
                      visible={visible}
                      total={total}
                      sortField={filters.sort_field ?? sortOptions[0] ?? 'score'}
                      sortOptions={sortOptions}
                      onSortFieldChange={(next) =>
                        setFilters({
                          ...filters,
                          sort_field: next,
                          sort_direction: getDefaultSortDirection(next),
                        })
                      }
                      // BRAND-9514: enter-selection control lives beside the result
                      // count. Link-driven gating; the enclosing block already
                      // guarantees showFilterChrome/!selectionMode/!setup/!formOpen.
                      action={
                        hasBulkRejectLink ? (
                          <PillButton
                            type="button"
                            onClick={() => setSelectionMode(true)}
                            data-testid="enter-selection-mode"
                          >
                            <FormattedMessage id="creatorSearch.vibeCheck.selection.enter" />
                          </PillButton>
                        ) : undefined
                      }
                    />
                  </>
                )}

                <ListWrapper>{renderContent()}</ListWrapper>
              </>
            )}
          </>
        )}
      </Container>
    </>
  )
}

export { AgentSourcedCreatorListView }
export default AgentSourcedCreatorListView
